import { ColumnDef } from '@tanstack/react-table';
import { t } from 'i18next';
import { Fingerprint, Hash, UserRoundCheck } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { CopyButton } from '@/components/ui/copy-button';
import {
  DataTableFilter,
  RowDataWithActions,
} from '@/components/ui/data-table';
import { DataTableColumnHeader } from '@/components/ui/data-table/data-table-column-header';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  AdminClient,
  AdminClientProvider,
  AdminClientStatus,
} from '@activepieces/shared';

export const useAdminClientsTable = () => {
  const columns: ColumnDef<RowDataWithActions<AdminClient>>[] = [
    {
      accessorKey: 'companyName',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('Client')} />
      ),
      cell: ({ row }) => (
        <Tooltip>
          <TooltipTrigger asChild>
            <span>{row.original.companyName ?? row.original.name}</span>
          </TooltipTrigger>
          <TooltipContent className="flex items-center gap-1.5">
            <span>{row.original.id}</span>
            <CopyButton
              textToCopy={row.original.id}
              variant="ghost"
              size="icon"
              className="h-5 w-5"
              withoutTooltip
            />
          </TooltipContent>
        </Tooltip>
      ),
    },
    {
      accessorKey: 'globalCode',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('Code')} />
      ),
      cell: ({ row }) => row.original.globalCode ?? 'N/A',
    },
    {
      accessorKey: 'externalCustomerId',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('Client ID')} />
      ),
      cell: ({ row }) => row.original.externalCustomerId ?? 'N/A',
    },
    {
      accessorKey: 'usersCount',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('Users')} />
      ),
    },
    {
      accessorKey: 'status',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('Status')} />
      ),
      cell: ({ row }) => (
        <Badge
          variant="outline"
          className={
            row.original.status === AdminClientStatus.ACTIVE
              ? 'bg-green-100 text-green-700 border-green-200'
              : 'bg-red-100 text-red-700 border-red-200'
          }
        >
          {row.original.status === AdminClientStatus.ACTIVE
            ? t('Active')
            : t('Inactive')}
        </Badge>
      ),
    },
    {
      accessorKey: 'isInternalKizeo',
    },
    {
      accessorKey: 'provider',
    },
  ];

  const filters: DataTableFilter<keyof AdminClient>[] = [
    {
      type: 'input',
      title: t('Search'),
      accessorKey: 'companyName',
      options: [],
    },
    {
      type: 'input',
      title: t('Client ID'),
      accessorKey: 'externalCustomerId',
      icon: Hash,
      options: [],
    },
    {
      type: 'select',
      title: t('Status'),
      accessorKey: 'status',
      icon: UserRoundCheck,
      options: [
        { label: t('Active'), value: AdminClientStatus.ACTIVE },
        { label: t('Inactive'), value: AdminClientStatus.INACTIVE },
      ],
    },
    {
      type: 'select',
      title: t('Provider'),
      accessorKey: 'provider',
      icon: Fingerprint,
      options: [
        { label: t('Kizeo'), value: AdminClientProvider.KIZEO },
        { label: t('Google'), value: AdminClientProvider.GOOGLE },
      ],
    },
    {
      type: 'select',
      title: t('Internal Kizeo'),
      accessorKey: 'isInternalKizeo',
      options: [
        { label: t('Yes'), value: 'true' },
        { label: t('No'), value: 'false' },
      ],
    },
  ];

  return { columns, filters };
};
